import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import {
  Badge,
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  FactCheckOutlined,
  FileDownloadOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  PrintOutlined,
  ViewModuleOutlined,
  AssignmentIndOutlined,
} from "@mui/icons-material";
import ImageAPI from "../../../api/ImageAPI";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import { listingStatusTabsStyles } from "../../../styles/listingStatusTabsSx";
import FormTextField from "../../components/common/form/FormTextField";
import PodAllocatedOrderListTable from "../../components/pod-order/PodAllocatedOrderListTable";
import PodOrderPrintOutwardDrawer from "../../components/pod-order/PodOrderPrintOutwardDrawer";
import PodOrderPrintInwardDrawer from "../../components/pod-order/PodOrderPrintInwardDrawer";
import {
  formatPodOrderBatchInventoryAllocateErrors,
  getPodOrderBatchInventoryAllocateFailures,
  parsePodOrderBatchInventoryAllocateResult,
  PodOrderPrintOutwardConfirmPayload,
} from "../../../models/PodOrder/PodOrder";
import {
  POD_ORDER_PROGRESS_STAGE_LABELS,
  PodOrderProgressStage,
} from "../../../models/PodOrder/PodOrderProgressStage";
import { formatDate, formatDateWithoutTime } from "../../components/common/functions/formatDate";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import AppSearchDropdownMulti from "../../components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  getPodPrintingTypeLabel,
  POD_PRINTING_TYPE_OPTIONS,
  toPodPrintingTypeName,
} from "../../../models/PodOrder/PodPrintingType";

const POD_ORDER_LIST_DEFAULT_TAB_ID = "allocated-orders";

const PodAllocatedOrderList: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { podOrderStore, userStore, orderStore } = useStores();
  const orderSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const { showError, showSuccess, showWarning } = useNotification();
  const { params, selectedOrders } = podOrderStore;
  const podOrderStatusTabs = useMemo(
    () => [
      {
        id: "all",
        label: "All Orders",
        status: "",
        Icon: ViewModuleOutlined,
        iconColor: theme.palette.customColors?.greyTextColor,
        action: null,
        selectionEnabled: false,
      },
      {
        id: "unassigned",
        label: "UNASSIGNED",
        status: PodOrderProgressStage.UNASSIGNED,
        Icon: AssignmentIndOutlined,
        iconColor: theme.palette.customColors?.darkBlue,
        action: {
          label: "Allocate",
          targetStatus: PodOrderProgressStage.INVENTORY_ALLOCATED,
        },
        selectionEnabled: true,
      },
      {
        id: "allocated-orders",
        label: "Allocated orders",
        status: PodOrderProgressStage.INVENTORY_ALLOCATED,
        Icon: Inventory2Outlined,
        iconColor: theme.palette.customColors?.darkBlue,
        action: {
          label: "Print Outward",
          targetStatus: PodOrderProgressStage.OUT_FOR_PRINTING,
        },
        selectionEnabled: true,
      },
      {
        id: "out-for-printing",
        label: "Print Outward",
        status: PodOrderProgressStage.OUT_FOR_PRINTING,
        Icon: PrintOutlined,
        iconColor: theme.palette.customColors?.pinkDark,
        action: {
          label: "Print Inward",
          targetStatus: PodOrderProgressStage.PARTIALLY_PRINT_INWARD,
        },
        selectionEnabled: true,
      },
      {
        id: "printing-completed",
        label: "Partially Print Inward",
        status: PodOrderProgressStage.PARTIALLY_PRINT_INWARD,
        Icon: FactCheckOutlined,
        iconColor: theme.palette.customColors?.greenDark,
        action: null,
        selectionEnabled: false,
      },
      {
        id: "shipment-created",
        label: "Shipment Created",
        status: PodOrderProgressStage.SHIPMENT_CREATED,
        Icon: LocalShippingOutlined,
        iconColor: theme.palette.customColors?.darkBlue,
        action: null,
        selectionEnabled: false,
      },
    ],
    [theme],
  );
  const defaultTab =
    podOrderStatusTabs.find(tab => tab.id === POD_ORDER_LIST_DEFAULT_TAB_ID) ??
    podOrderStatusTabs[1];
  const activeTab =
    podOrderStatusTabs.find(tab => tab.id === params.tab_id) ?? defaultTab;

  const listQueryKey = [
    params.page,
    params.limit,
    params.tab_id,
    params.status,
    params.sku,
    params.start_date,
    params.end_date,
    params.sort_by,
    params.sort_type,
    (params.order_ids ?? []).join(","),
    params.printing_type ?? "",
  ].join("|");

  const handleOrderSearch = (searchText?: string) => {
    if (orderSearchTimerRef.current) clearTimeout(orderSearchTimerRef.current);
    if (searchText && searchText.length > 0) {
      orderSearchTimerRef.current = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch(() => { });
      }, 500);
    }
  };


  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabIdParam = searchParams.get("tab_id");
    const resolvedTabId =
      tabIdParam && podOrderStatusTabs.some(tab => tab.id === tabIdParam)
        ? tabIdParam
        : POD_ORDER_LIST_DEFAULT_TAB_ID;
    const selectedTab =
      podOrderStatusTabs.find(tab => tab.id === resolvedTabId) ?? defaultTab;
    podOrderStore.initializeParamsFromSearch(
      searchParams,
      selectedTab.id,
      selectedTab.status,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (!userStore.hasPermission([EmployeeRoles.VIEW_ALLOCATED_POD_ORDER])) return;
    podOrderStore
      .getPodOrders()
      .then(res => {
        if (!res && podOrderStore.error) showError("Error", podOrderStore.error);
      })
      .catch(() => {
        showError("Error", "Error fetching POD Orders");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQueryKey]);


  useEffect(() => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set("page", params.page.toString());
    if (params.limit) searchParams.set("limit", params.limit.toString());
    if (params.tab_id) searchParams.set("tab_id", params.tab_id);
    if (params.status) searchParams.set("status", params.status);
    if (params.sku) searchParams.set("sku", params.sku);
    if (params.start_date) searchParams.set("start_date", params.start_date);
    if (params.end_date) searchParams.set("end_date", params.end_date);
    if (params.sort_by) searchParams.set("sort_by", params.sort_by);
    if (params.sort_type) searchParams.set("sort_type", params.sort_type);
    params.order_ids?.forEach(id => searchParams.append("order_id", id.toString()));
    if (params.printing_type) {
      searchParams.set("printing_types", params.printing_type);
    }

    const nextSearch = searchParams.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (nextSearch !== currentSearch) {
      navigate({ search: nextSearch }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQueryKey, navigate]);

  const handleStatusTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    const selectedTab =
      podOrderStatusTabs.find(tab => tab.id === newValue) ?? podOrderStatusTabs[0];
    podOrderStore.setParams({
      tab_id: selectedTab.id,
      status: selectedTab.status,
      page: 1,
    });
  };

  const handleExport = () => {
    const orders = podOrderStore.podOrderListingResponse.list;
    if (orders.length === 0) {
      showError("Error", "No data to export.");
      return;
    }

    const escapeCsv = (value: unknown) => {
      const cell = String(value ?? "").replace(/"/g, '""');
      return `"${cell}"`;
    };

    const getStageLabel = (status: string) => {
      if (status in POD_ORDER_PROGRESS_STAGE_LABELS) {
        return POD_ORDER_PROGRESS_STAGE_LABELS[status as PodOrderProgressStage];
      }
      return status.replace(/_/g, " ");
    };

    const headers = [
      "Order ID",
      "Product ID",
      "Product Name",
      "Variant",
      "Order Qty",
      "Printing Type",
      "Order Date",
      "Stage",
    ];

    const rows = orders.map(order => {
      const orderDate = order.raw_inward_date ?? order.print_outward_date;
      return [
        order.order_id,
        order.product_id,
        order.product_name?.trim() || "",
        order.sku?.trim() || order.variant_name?.trim() || "",
        order.ordered_qty,
        getPodPrintingTypeLabel(
          order.printing_type ? toPodPrintingTypeName(order.printing_type) : "",
        ) || order.printing_type || "",
        orderDate
          ? formatDateWithoutTime(orderDate, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          : "",
        getStageLabel(order.status),
      ];
    });

    const csvContent = [
      headers.map(escapeCsv).join(","),
      ...rows.map(row => row.map(escapeCsv).join(",")),
    ].join("\n");

    const now = formatDate(new Date());
    const safeTab = activeTab.label.replace(/\s+/g, "_");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Pod_Orders_${safeTab}_${now}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleAllocateSelected = () => {
    podOrderStore.setConfirmLoading(true);
    podOrderStore
      .batchInventoryAllocateSelected()
      .then(res => {
        if (res == null) {
          showError("Error", "No rows selected.");
          return;
        }
        if (!res.success) {
          showError("Error", res.message ?? "Failed to allocate inventory.");
          return;
        }

        const allocateResult = parsePodOrderBatchInventoryAllocateResult(
          res.result,
        );
        const failures = getPodOrderBatchInventoryAllocateFailures(allocateResult);

        if (allocateResult && failures.length > 0) {
          const errorDetails = formatPodOrderBatchInventoryAllocateErrors(
            failures,
          );
          const { succeeded, failed, total } = allocateResult.summary;

          if (succeeded > 0) {
            showWarning(
              `${succeeded} of ${total} allocated. ${failed} failed.\n${errorDetails}`,
            );
            podOrderStore.clearSelection();
            return podOrderStore.getPodOrders();
          }

          showError(
            "Allocation failed",
            errorDetails || res.message || "Failed to allocate inventory.",
          );
          return podOrderStore.getPodOrders();
        }

        showSuccess(res.message || "Inventory allocated successfully.");
        podOrderStore.clearSelection();
        return podOrderStore.getPodOrders();
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          error?.message ||
          "Failed to allocate inventory.",
        );
      })
      .finally(() => {
        podOrderStore.setConfirmLoading(false);
      });
  };

  const handleTabActionClick = () => {
    const action = activeTab.action;
    if (!action || selectedOrders.length === 0) return;
    if (action.targetStatus === PodOrderProgressStage.INVENTORY_ALLOCATED) {
      handleAllocateSelected();
      return;
    }
    if (action.targetStatus === PodOrderProgressStage.OUT_FOR_PRINTING) {
      podOrderStore.openPrintOutwardDrawer().then(opened => {
        if (!opened) {
          podOrderStore.setPrintOutwardDrawerOpen(false);
          showError(
            "Error",
            podOrderStore.selectedOrders.length === 0
              ? "No rows selected."
              : "Failed to load printing files.",
          );
        }
      });
      return;
    }
    if (action.targetStatus === PodOrderProgressStage.PARTIALLY_PRINT_INWARD) {
      if (!podOrderStore.openPrintInwardDrawer()) {
        showError("Error", "No rows selected.");
      }
    }
  };

  const handlePrintOutwardConfirm = (payload: PodOrderPrintOutwardConfirmPayload) => {
    podOrderStore.setPrintOutwardError(null);
    podOrderStore.setConfirmLoading(true);
    podOrderStore
      .batchPrintOutwardSelected(payload)
      .then(res => {
        if (res == null) {
          podOrderStore.setPrintOutwardError("No rows selected.");
          return;
        }
        if (res.success) {
          showSuccess(res.message || "Print outward submitted successfully.");
          podOrderStore.clearSelection();
          return podOrderStore.getPodOrders().then(() => {
            podOrderStore.setPrintOutwardDrawerOpen(false);
          });
        }
        podOrderStore.setPrintOutwardError(
          res.message ?? "Failed to submit print outward.",
        );
      })
      .catch((error: any) => {
        podOrderStore.setPrintOutwardError(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to submit print outward.",
        );
      })
      .finally(() => {
        podOrderStore.setConfirmLoading(false);
      });
  };

  const handlePrintInwardConfirm = () => {
    if (!podOrderStore.validatePrintInwardForm()) return;

    podOrderStore.setPrintInwardError(null);
    podOrderStore.setConfirmLoading(true);
    const imageAPI = new ImageAPI();

    const uploadRejectionImages = podOrderStore.printInwardLines.reduce(
      (chain, line) =>
        chain.then(() => {
          const files = podOrderStore.getPrintInwardRejectionFiles(line);
          if (files.length === 0) return;
          return imageAPI
            .uploadImage(files, "products")
            .then(uploadedUrls =>
              podOrderStore.setPrintInwardRejectionImageUrls(line, uploadedUrls),
            );
        }),
      Promise.resolve(),
    );

    uploadRejectionImages
      .then(() => podOrderStore.batchPrintInwardSelected())
      .then(res => {
        if (res == null) {
          podOrderStore.setPrintInwardError(
            "No rows selected or missing print outward id.",
          );
          return;
        }
        if (res.success) {
          showSuccess(res.message || "Print inward submitted successfully.");
          podOrderStore.clearSelection();
          return podOrderStore.getPodOrders().then(() => {
            podOrderStore.setPrintInwardDrawerOpen(false);
          });
        }
        podOrderStore.setPrintInwardError(
          res.message ?? "Failed to submit print inward.",
        );
      })
      .catch((error: any) => {
        podOrderStore.setPrintInwardError(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to submit print inward.",
        );
      })
      .finally(() => {
        podOrderStore.setConfirmLoading(false);
      });
  };

  if (!userStore.hasPermission([EmployeeRoles.VIEW_ALLOCATED_POD_ORDER])) {
    return <PermissionBlockLabel />;
  }

  return (
    <Grid2 size={{ xs: 12 }}>
      <Paper elevation={0} sx={{
        borderRadius: "4px", mt: "0.6rem",
        boxShadow: "rgba(0,0,0,0.25) 0 0.0625em 0.0625em, rgba(0,0,0,0.25) 0 0.125em 0.5em, rgba(255,255,255,0.1) 0 0 0 1px inset",
      }}>
        <Box sx={{ px: 4, py: 2, minHeight: { xs: "60vh", md: window.innerHeight - 110 } }}>

          <Box sx={{
            display: "flex", justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" }, gap: 2,
          }}>
            <Box>
              <CustomTypography variant="heading3">POD Orders</CustomTypography>
              <CustomTypography variant="body2" color={theme.palette.customColors?.greyTextColor}>
                Track POD Orders through their production and shipping lifecycle.
              </CustomTypography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
              {activeTab.action && (
                <Badge
                  badgeContent={selectedOrders.length}
                  invisible={selectedOrders.length === 0}
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.7rem",
                      minWidth: 18,
                      height: 18,
                      backgroundColor: theme.palette.customColors?.white,
                      color: theme.palette.customColors?.darkBlue,
                      border: `1px solid ${theme.palette.customColors?.blackBorder}`,
                      fontWeight: 600,
                      right: 5,
                      top: 2,
                    },
                  }}
                >
                  <AppButton
                    variant="primary"
                    label={activeTab.action.label}
                    disabled={
                      !userStore.hasPermission([EmployeeRoles.MANAGE_ALLOCATED_POD_PRINT]) ||
                      selectedOrders.length === 0 ||
                      podOrderStore.confirmLoading
                    }
                    onClick={handleTabActionClick}
                  />
                </Badge>
              )}

              <AppButton
                variant="grayscale"
                label="Export"
                disabled={podOrderStore.loading || podOrderStore.podOrderListingResponse.list.length === 0}
                startIcon={<FileDownloadOutlined />}
                onClick={handleExport}
              />
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Box
              onClick={() => podOrderStore.toggleFiltersOpen()}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 0.5,
                cursor: "pointer",
                borderBottom: podOrderStore.filtersOpen ? "1px solid" : "none",
                borderColor: "divider",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CustomTypography variant="heading6">
                Search & Filters
              </CustomTypography>
              <IconButton size="small">
                {podOrderStore.filtersOpen ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={podOrderStore.filtersOpen} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: { xs: 2, md: 1 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: "200px" }}>
                    <FormTextField
                      id="pod-order-search"
                      placeholder="Search by SKU..."
                      type="search"
                      variant="outlined"
                      value={podOrderStore.draftSearch}
                      marginBottom={0}
                      sx={{
                        mb: 0,
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          minHeight: 40,
                          boxSizing: "border-box",
                        },
                      }}
                      onChange={(_, value) => podOrderStore.setDraftSearch(value ?? "")}
                      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === "Enter") podOrderStore.applySearch();
                      }}
                    />
                  </Box>
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={() => podOrderStore.applySearch()}
                    customStyles={{ minWidth: 140, height: 40, minHeight: 40 }}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear All"
                    onClick={() => podOrderStore.clearFilters()}
                    customStyles={{ minWidth: 140, height: 40, minHeight: 40 }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1.5,
                    flexWrap: "nowrap",
                    alignItems: "center",
                    marginTop: 2,
                    justifyContent: "space-between",
                  }}
                >

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      flexWrap: "nowrap",
                      alignItems: "center",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ flex: "0 0 180px", minWidth: 140, maxWidth: 220 }}>
                      <AppSearchDropdownMulti
                        minWidth="140px"
                        isMultiSelect={false}
                        title="Search Order"
                        fontSize="12px"
                        value={podOrderStore.draftOrderIds.map(id => ({
                          id: String(id),
                          value: String(id),
                        }))}
                        list={orderStore.orderIds.map(item => ({
                          id: String(item),
                          value: String(item),
                        }))}
                        local={false}
                        handleSearch={handleOrderSearch}
                        handleSelect={(items: FilterValue[]) => {
                          podOrderStore.setDraftOrderIds(
                            items.map(item => Number(item.id)),
                          );
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: "0 0 200px", minWidth: 160, maxWidth: 260 }}>
                      <AppSearchDropdownMulti
                        minWidth="140px"
                        isMultiSelect={false}
                        title="Select Printing Type"
                        fontSize="12px"
                        value={
                          podOrderStore.draftPrintingType
                            ? [
                              {
                                id: podOrderStore.draftPrintingType,
                                value: getPodPrintingTypeLabel(
                                  podOrderStore.draftPrintingType,
                                ),
                              },
                            ]
                            : []
                        }
                        list={POD_PRINTING_TYPE_OPTIONS}
                        local={true}
                        handleSelect={(items: FilterValue[]) => {
                          podOrderStore.setDraftPrintingType(
                            items[0]?.id?.toString() ?? "",
                          );
                        }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={() => podOrderStore.applyFilters()}
                      customStyles={{ minWidth: 140, height: 40, minHeight: 40 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Paper>

          <Tabs
            value={activeTab.id}
            onChange={handleStatusTabChange}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={t => ({
              ...listingStatusTabsStyles(t),
              mt: 1,
              "& .MuiTab-iconWrapper": { mr: 0.5, mb: 0 },
            })}>
            {podOrderStatusTabs.map(tab => (
              <Tab
                key={tab.id}
                value={tab.id}
                id={`pod-tab-${tab.id}`}
                icon={
                  <tab.Icon
                    sx={{
                      fontSize: { xs: 16, sm: 18 },
                      color: `${tab.iconColor} !important`,
                    }}
                  />
                }
                iconPosition="start"
                label={tab.label.toUpperCase()}
              />
            ))}
          </Tabs>

          <PodAllocatedOrderListTable
            loading={podOrderStore.loading}
            orders={podOrderStore.podOrderListingResponse.list}
            selectionEnabled={activeTab.selectionEnabled}
            isRowSelected={order => podOrderStore.isOrderSelected(order)}
            sortBy={params.sort_by}
            sortType={params.sort_type}
            page={params.page ?? 1}
            limit={params.limit ?? 30}
            totalCount={podOrderStore.podOrderListingResponse.total_count}
            onSortChange={column => podOrderStore.handleSortChange(column)}
            areAllSelected={() => podOrderStore.areAllSelected()}
            areSomeSelected={() => podOrderStore.areSomeSelected()}
            onToggleSelectAll={() =>
              podOrderStore.areAllSelected()
                ? podOrderStore.clearSelection()
                : podOrderStore.selectAll()
            }
            onToggleRow={order => podOrderStore.toggleRow(order)}
            onPageChange={(_, page) => podOrderStore.handlePageChange(page + 1)}
            onRowsPerPageChange={e =>
              podOrderStore.handleRowsPerPageChange(parseInt(e.target.value, 10))
            }
          />
        </Box>
      </Paper>

      <PodOrderPrintOutwardDrawer
        open={podOrderStore.printOutwardDrawerOpen}
        onClose={() =>
          !podOrderStore.confirmLoading && podOrderStore.setPrintOutwardDrawerOpen(false)
        }
        submitting={podOrderStore.confirmLoading}
        onConfirm={handlePrintOutwardConfirm}
      />

      <PodOrderPrintInwardDrawer
        open={podOrderStore.printInwardDrawerOpen}
        onClose={() =>
          !podOrderStore.confirmLoading && podOrderStore.setPrintInwardDrawerOpen(false)
        }
        submitting={podOrderStore.confirmLoading}
        onConfirm={handlePrintInwardConfirm}
      />

    </Grid2>
  );
};

export default observer(PodAllocatedOrderList);
